import React from 'react';
import {
  checkFunnelConservation,
  type FunnelConservation,
  type FunnelCounters,
} from '../../lib/funnel';

interface AlertFlowLedgerProps {
  counters: FunnelCounters;
}

const n = (value: number) => value.toLocaleString('en-US');

function Row({
  label,
  inputs,
  expected,
  actual,
  check,
}: {
  label: string;
  inputs: string;
  expected: number;
  actual: number;
  check: FunnelConservation['candidates'];
}) {
  return (
    <div className={`c3-ledger-row${check.ok ? '' : ' is-broken'}`}>
      <span className="c3-ledger-label">{label}</span>
      <span className="c3-ledger-inputs">
        <b>{inputs}</b>
      </span>
      <span className="c3-ledger-sum">
        {n(actual)} / {n(expected)}
      </span>
      <span className={`c3-ledger-dot${check.ok ? ' ok' : ' bad'}`} title={check.ok ? 'ok' : check.diagnosis} />
      {!check.ok && <span className="c3-ledger-diag">{check.diagnosis}</span>}
    </div>
  );
}

const AlertFlowLedger: React.FC<AlertFlowLedgerProps> = ({ counters }) => {
  const conservation = checkFunnelConservation(counters);
  const c = counters.candidates;
  const v = counters.verdicts;
  const t = counters.threats;
  return (
    <div className="c3-ledger">
      <Row
        label="Candidates"
        inputs={`${n(c.pending)} pending + ${n(c.analyzing)} analyzing + ${n(c.error)} error + ${n(c.duplicated)} duplicated + ${n(c.unique)} unique`}
        expected={c.registered}
        actual={c.pending + c.analyzing + c.error + c.duplicated + c.unique}
        check={conservation.candidates}
      />
      <Row
        label="Verdicts"
        inputs={`${n(v.baseline)} baseline + ${n(v.suppressed)} suppressed + ${n(v.escalated)} escalated + ${n(v.threat)} threat`}
        expected={c.unique}
        actual={v.baseline + v.suppressed + v.escalated + v.threat}
        check={conservation.verdicts}
      />
      <Row
        label="Threats"
        inputs={`${n(t.inCampaign)} in campaign + ${n(t.standalone)} standalone`}
        expected={t.total}
        actual={t.inCampaign + t.standalone}
        check={conservation.threats}
      />
      <div className="c3-ledger-row">
        <span className="c3-ledger-label">Campaigns</span>
        <span className="c3-ledger-inputs">
          <b>
            {n(counters.campaigns.open)} open + {n(counters.campaigns.closed)} closed
          </b>
        </span>
        <span className="c3-ledger-sum">{n(counters.campaigns.total)}</span>
        <span className="c3-ledger-dot ok" />
      </div>
      <div className="c3-ledger-row">
        <span className="c3-ledger-label">Actions</span>
        <span className="c3-ledger-inputs">
          <b>
            {n(counters.actions.pending)} pending + {n(counters.actions.confirmed)} confirmed +{' '}
            {n(counters.actions.inProgress)} in progress + {n(counters.actions.completed)} completed +{' '}
            {n(counters.actions.cancelled)} cancelled + {n(counters.actions.reverted)} reverted
          </b>
        </span>
        <span className="c3-ledger-sum">{n(counters.actions.total)}</span>
        <span className="c3-ledger-dot ok" />
      </div>
    </div>
  );
};

export default AlertFlowLedger;
